import clientAxios from "../client";

import { formatQueryString } from "@/common/utils/common";

import type { QueryType, FileType } from "@/common/types/common";
import type {
  EvaluationPayloadType,
  EvaluationType,
} from "@/common/types/evaluation";

export async function evaluationList(
  account_id: string,
  query?: { [key: string]: any },
) {
  const queryString = formatQueryString(query);

  return await clientAxios.get(
    `account/${account_id}/evaluation/list${queryString}`,
  );
}

export async function evaluationListByMember(
  account_id: string,
  member_id: string,
  query?: QueryType,
) {
  const queryString = formatQueryString(query);

  return await clientAxios.get(
    `account/${account_id}/member/${member_id}/evaluation/list${queryString}`,
  );
}

export async function evaluationCockpit(account_id: string) {
  return await clientAxios.get(`account/${account_id}/evaluation/cockpit`);
}

export async function evaluationAdd(
  account_id: string,
  payload: EvaluationPayloadType,
) {
  return await clientAxios.post(
    `account/${account_id}/evaluation/create`,
    payload,
  );
}

export async function evaluationGet(evaluation_id: string) {
  return await clientAxios.get(`account/evaluation/${evaluation_id}/show`);
}

export async function evaluationPut(
  evaluation_id: string,
  payload: EvaluationPayloadType,
) {
  return await clientAxios.put(
    `account/evaluation/${evaluation_id}/edit`,
    payload,
  );
}

export async function evaluationPatch(
  evaluation_id: string,
  payload: Partial<EvaluationType>,
) {
  return await clientAxios.patch(`account/evaluation/${evaluation_id}/patch`, {
    payload,
  });
}

export async function evaluationStart(evaluation_id: string) {
  return await clientAxios.post(
    `account/evaluation/${evaluation_id}/start`,
    null,
  );
}

export async function evaluationCancel(evaluation_id: string) {
  return await clientAxios.post(
    `account/evaluation/${evaluation_id}/cancel`,
    null,
  );
}

export async function evaluationFinish(evaluation_id: string) {
  return await clientAxios.post(
    `account/evaluation/${evaluation_id}/finish`,
    null,
  );
}

export async function evaluationDel(evaluation_id: string) {
  return await clientAxios.del(`account/evaluation/${evaluation_id}/delete`);
}

// LABORATORY
export async function evaluationLaboratoryList(
  evaluation_id: string,
  query?: QueryType,
) {
  const queryString = formatQueryString(query);

  return await clientAxios.get(
    `account/evaluation/${evaluation_id}/laboratory/list${queryString}`,
  );
}

export async function evaluationLaboratoryAdd(
  evaluation_id: string,
  laboratory_id: string,
) {
  return await clientAxios.post(
    `account/evaluation/${evaluation_id}/laboratory/add`,
    { laboratory_id },
  );
}

export async function evaluationLaboratoryDel(
  evaluation_id: string,
  laboratory_id: string,
) {
  return await clientAxios.del(
    `account/evaluation/${evaluation_id}/laboratory/${laboratory_id}/delete`,
  );
}

// SAMPLES
export async function evaluationSampleList(
  evaluation_id: string,
  query?: QueryType,
) {
  const queryString = formatQueryString(query);

  return await clientAxios.get(
    `account/evaluation/${evaluation_id}/sample/list${queryString}`,
  );
}

export async function evaluationSampleAdd(
  evaluation_id: string,
  sample_id: string,
) {
  return await clientAxios.post(
    `account/evaluation/${evaluation_id}/sample/add`,
    { sample_id },
  );
}

export async function evaluationSampleDel(
  evaluation_id: string,
  sample_id: string,
) {
  return await clientAxios.del(
    `account/evaluation/${evaluation_id}/sample/${sample_id}/delete`,
  );
}

// EQUIPMENT
export async function evaluationEquipmentList(
  evaluation_id: string,
  query?: QueryType,
) {
  const queryString = formatQueryString(query);

  return await clientAxios.get(
    `account/evaluation/${evaluation_id}/equipment/list${queryString}`,
  );
}

export async function evaluationEquipmentAdd(
  evaluation_id: string,
  equipment_id: string,
) {
  return await clientAxios.post(
    `account/evaluation/${evaluation_id}/equipment/add`,
    { equipment_id },
  );
}

export async function evaluationEquipmentDel(
  evaluation_id: string,
  equipment_id: string,
) {
  return await clientAxios.del(
    `account/evaluation/${evaluation_id}/equipment/${equipment_id}/delete`,
  );
}

// COMPANY
export async function evaluationCompanyShow(
  evaluation_id: string,
  company_id: string,
) {
  return await clientAxios.get(
    `account/evaluation/${evaluation_id}/company/${company_id}/show`,
  );
}

export async function evaluationCompanyAdd(
  evaluation_id: string,
  company_id: string,
) {
  return await clientAxios.post(
    `account/evaluation/${evaluation_id}/company/add`,
    { company_id },
  );
}

export async function evaluationCompanyPut(
  evaluation_id: string,
  company_id: string,
  payload: {
    address_id?: string;
    contact_id?: string;
  },
) {
  return await clientAxios.put(
    `account/evaluation/${evaluation_id}/company/${company_id}/edit`,
    payload,
  );
}

export async function evaluationCompanyDel(
  evaluation_id: string,
  company_id: string,
) {
  return await clientAxios.del(
    `account/evaluation/${evaluation_id}/company/${company_id}/delete`,
  );
}

// EMPLOYEES

export async function evaluationEmployeeList(
  evaluation_id: string,
  query?: QueryType,
) {
  const queryString = formatQueryString(query);

  return await clientAxios.get(
    `account/evaluation/${evaluation_id}/employee/list${queryString}`,
  );
}

export async function evaluationEmployeeAdd(
  evaluation_id: string,
  employee_id: string,
) {
  return await clientAxios.post(
    `account/evaluation/${evaluation_id}/employee/add`,
    { employee_id },
  );
}

export async function evaluationEmployeeDel(
  evaluation_id: string,
  employee_id: string,
) {
  return await clientAxios.del(
    `account/evaluation/${evaluation_id}/employee/${employee_id}/delete`,
  );
}

export async function evaluationEmployeeSampleUpload(
  evaluation_id: string,
  payload: FileType,
) {
  return await clientAxios.post(
    `account/evaluation/${evaluation_id}/sample/csv`,
    payload,
  );
}

export async function evaluationEmployeeEquipmentUpload(
  evaluation_id: string,
  payload: FileType,
) {
  return await clientAxios.post(
    `account/evaluation/${evaluation_id}/equipment/csv`,
    payload,
  );
}

export async function evaluationEmployeeEmployeeUpload(
  evaluation_id: string,
  payload: FileType,
) {
  return await clientAxios.post(
    `account/evaluation/${evaluation_id}/employee/csv`,
    payload,
  );
}
